import React, { useState, useEffect } from 'react';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { MenuItem, Select, TextField } from '@mui/material';
import styles from './ChatBotPage.module.scss';
import { Button, ConfirmModal, openNotification } from 'tt-ui-lib/core';
import { Table } from './Components/Table';
import { AddQuestion } from './Components/AddQuestion';
import Box from '@mui/material/Box';
import {
  createBlock,
  deleteBlock,
  fetchCategories,
  fetchTableData,
  updateBlock,
  updateTableSequence,
} from '../../../../api/admin/endpoints/chatBot';
import { LoaderOn } from '../../../../components/Loader';
import { FormProvider, useForm } from 'react-hook-form';
import { arrayMove } from '@dnd-kit/sortable';

const ChatBotPage = () => {
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isFetchTable, setIsFetchTable] = useState(false);
  const [loadingSections, setLoadingSections] = useState(false);

  const [sections, setSections] = useState([]);
  const [blocks, setBlocks] = useState([]);

  const [selectedSection, setSelectedSection] = useState('');
  const [selectedBlock, setSelectedBlock] = useState('');

  const [openAddCloseConfirm, setOpenAddCloseConfirm] = useState(false);
  const [newAddConfirmValue, setNewAddConfirmValue] = useState('');
  const [newAddConfirmSectionValue, setNewAddConfirmSectionValue] = useState('');
  const [isAddQuestion, setIsAddQuestion] = useState(false);
  const [isEditQuestion, setIsEditQuestion] = useState(false);
  const [idEditQuestion, setIdEditQuestion] = useState(false);

  const [openEditConfirmBlock, setOpenEditConfirmBlock] = useState(false);
  const [openEditBlock, setOpenEditBlock] = useState(false);
  const [idEditBlock, setIdEditBlock] = useState('');
  const [idDeleteBlock, setIdDeleteBlock] = useState('');
  const [nameEditBlock, setNameEditBlock] = useState('');
  const [openAddBlock, setOpenAddBlock] = useState(false);
  const [nameAddBlock, setNameAddBlock] = useState('');
  const [languages, setLanguages] = useState([
    { value: 'en', name: 'EN' },
    { value: 'it', name: 'IT', disabled: true },
    { value: 'fr', name: 'FR', disabled: true },
  ]);
  const [queryFilter, setQueryFilter] = useState({
    order_dir: 'desc',
    f: '',
    is_active: '',
  });
  const [filter, setFilter] = useState({});

  const methods = useForm();

  const fetchSections = () => {
    setLoadingSections(true);

    fetchCategories('')
      .then((response) => {
        if (response.status === 200) {
          const respSections = response.data.data;
          setSections(respSections);
          setSelectedSection(respSections[0].id);
        }
      })
      .catch((err) => {
        openNotification({
          message: err?.message || 'Error while making request',
          type: 'error',
        });
      })
      .finally(() => {
        setLoadingSections(false);
      });
  };

  const fetchBlocks = (sectionId) => {
    setLoadingSections(true);

    fetchCategories(sectionId)
      .then((response) => {
        if (response.status === 200) {
          const respBlocks = response.data.data;
          setBlocks(respBlocks);
        }
      })
      .catch((err) => {
        openNotification({
          message: err?.message || 'Error while making request',
          type: 'error',
        });
      })
      .finally(() => {
        setLoadingSections(false);
      });
  };

  const fetchTable = (query, selectedBlockId) => {
    setLoading(true);
    setTableData([]);

    const params = {
      ...query,
      'documentation_category_id[0]': selectedBlockId,
    };

    fetchTableData(params)
      .then((response) => {
        if (response.status === 200) {
          const respData = response.data.data;
          setTableData(respData.map((data, index) => ({ ...data, sort: index + 1 })));
        }
      })
      .catch((err) => {
        openNotification({
          message: err?.message || 'Error while making request',
          type: 'error',
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const createNewBlock = () => {
    setLoadingSections(true);

    createBlock(selectedSection, nameAddBlock)
      .then((response) => {
        if (response.status === 200 && response.data.success) {
          fetchBlocks(selectedSection);
          setNameAddBlock('');
          setOpenAddBlock(false);
        }
      })
      .catch((err) => {
        openNotification({
          message: err?.message || 'Error while making request',
          type: 'error',
        });
      })
      .finally(() => {
        setLoadingSections(false);
      });
  };

  const deleteCurrentBlock = () => {
    setLoadingSections(true);

    deleteBlock(selectedBlock)
      .then((response) => {
        if (response.status === 200 && response.data.success) {
          fetchBlocks(selectedSection);
          setIdDeleteBlock('');
        }
      })
      .catch((err) => {
        openNotification({
          message: err?.message || 'Error while making request',
          type: 'error',
        });
      })
      .finally(() => {
        setLoadingSections(false);
      });
  };

  const handleUpdateTableSequence = async (updatedList) => {
    await updateTableSequence(updatedList);
  };

  const updateCurrentBlock = () => {
    setOpenEditConfirmBlock(false);
    setLoadingSections(true);

    updateBlock(selectedSection, idEditBlock, nameEditBlock)
      .then((response) => {
        if (response.status === 200 && response.data.success) {
          fetchBlocks(selectedSection);
          setNameEditBlock('');
          setIdEditBlock('');
          setOpenEditBlock(false);
        }
      })
      .catch((err) => {
        openNotification({
          message: err?.message || 'Error while making request',
          type: 'error',
        });
      })
      .finally(() => {
        setLoadingSections(false);
      });
  };

  useEffect(() => {
    fetchSections();
  }, []);

  useEffect(() => {
    if (selectedSection) {
      fetchBlocks(selectedSection);
    }
  }, [selectedSection]);

  useEffect(() => {
    if (isFetchTable) {
      fetchTable(queryFilter, selectedBlock);
      setIsFetchTable(false);
    }
  }, [isFetchTable]);

  useEffect(() => {
    if (blocks.length > 0) {
      setSelectedBlock(blocks[0].id);
    }
  }, [blocks]);

  useEffect(() => {
    if (selectedBlock && queryFilter) {
      setIsFetchTable(true);
    }
  }, [selectedBlock, queryFilter]);

  const handleChangeSection = (event, newValue) => {
    if (isAddQuestion) {
      setOpenAddCloseConfirm(true);
      setNewAddConfirmSectionValue(newValue);
      return;
    }

    setSelectedSection(newValue);
    setSelectedBlock(newValue);
    setFilter({ f: '' });
    setQueryFilter({
      order_dir: 'desc',
      is_active: '',
      f: '',
    });
  };

  const handleChangeBlock = (event, newValue) => {
    if (isAddQuestion || isEditQuestion) {
      setOpenAddCloseConfirm(true);
      setNewAddConfirmValue(newValue);
      return;
    }

    setSelectedBlock(newValue);
    setFilter({ f: '' });
    setQueryFilter({
      order_dir: 'desc',
      is_active: '',
      f: '',
    });
  };

  const confirmExitFromAdd = () => {
    methods.reset();
    if (newAddConfirmValue) {
      setSelectedBlock(newAddConfirmValue);
    }

    if (newAddConfirmSectionValue) {
      setSelectedSection(newAddConfirmSectionValue);
    }

    setFilter({ f: '' });
    setQueryFilter({
      order_dir: 'desc',
      is_active: '',
      f: '',
    });

    setNewAddConfirmValue('');
    setNewAddConfirmSectionValue('');
  };

  const handleChangeSectionSelect = (event) => {
    handleChangeSection(event, event.target.value);
  };

  const handleChangeBlockSelect = (event) => {
    handleChangeBlock(event, event.target.value);
  };

  const onFinishAddBlock = () => {
    setIsAddQuestion(false);
    setIsEditQuestion(false);
    setIsFetchTable(true);
  };

  const reorder = (list, startIndex, endIndex) => {
    const newList = [...list];
    const movedItem = newList[startIndex];
    newList.splice(startIndex, 1);
    newList.splice(endIndex, 0, movedItem);

    const updatedItems = newList.map((item, index) => ({
      id: item.id,
      sort: index,
    }));

    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return { items: result, updatedItems };
  };

  const onDragEnd = async ({ active, over }) => {
    if (active.id !== over?.id) {
      setTableData((prevState) => {
        const activeIndex = prevState.findIndex((record) => record.sort === active?.id);
        const overIndex = prevState.findIndex((record) => record.sort === over?.id);
        const { updatedItems } = reorder(tableData, activeIndex, overIndex);
        handleUpdateTableSequence(updatedItems);
        return arrayMove(prevState, activeIndex, overIndex);
      });
    }
  };

  return (
    <>
      <div className={styles.pageContainer}>
        {sections.length > 0 ? (
          <div className={styles.tabsContainer}>
            <Tabs
              TabIndicatorProps={{ style: { backgroundColor: '#143471' } }}
              centered
              value={selectedSection}
              onChange={handleChangeSection}
              className={styles.tabs}
              scrollButtons
            >
              {sections.map((item) => (
                <Tab
                  value={item.id}
                  label={item.name}
                  className={[
                    styles.tabItem,
                    selectedSection === item.id ? styles.activeItem : null,
                  ]}
                  sx={{ margin: '0px 30px', padding: 0, color: '#ff001c' }}
                />
              ))}
            </Tabs>
            {blocks.length > 0 && (
              <Tabs
                TabIndicatorProps={{ style: { backgroundColor: '#143471' } }}
                centered
                value={selectedBlock}
                onChange={handleChangeBlock}
                className={[styles.tabs, styles.secondTabs]}
                scrollButtons
                variant="scrollable"
                sx={{
                  '.MuiTabs-scroller': {
                    display: 'flex',
                    justifyContent: 'center',
                    maxWidth: '70%',
                    webkitFlex: 'unset',
                    flex: 'unset',

                    '@media (max-width: 1400px)': {
                      maxWidth: '60%',
                    },
                    '& > div': {
                      width: '100%',
                    },
                  },
                }}
              >
                {loadingSections && <LoaderOn />}
                {blocks.map((item) => (
                  <Tab
                    value={item.id}
                    label={item.name}
                    className={[
                      styles.tabItem,
                      selectedBlock === item.id ? styles.activeItem : null,
                    ]}
                    sx={{ margin: '0px 30px', padding: 0, color: '#ff001c' }}
                  />
                ))}
              </Tabs>
            )}
            <Button
              type="link"
              className={styles.addBlockBtn}
              onClick={() => {
                setOpenAddBlock(true);
              }}
            >
              <div className={styles.buttonWithImage}>
                <img src="/images/admin/plusBlue.svg" alt="" /> <span>Add Block</span>
              </div>
            </Button>
          </div>
        ) : null}

        {sections.length > 0 ? (
          <div>
            <Select
              onChange={handleChangeSectionSelect}
              value={selectedSection}
              size="medium"
              className={styles.topSelect}
              sx={{ '.MuiOutlinedInput-notchedOutline': { border: 0 } }}
            >
              {sections.map((item) => (
                <MenuItem value={item.id}>
                  <span className={styles.topMenuItem}>{item.name}</span>
                </MenuItem>
              ))}
            </Select>

            <Select
              onChange={handleChangeBlockSelect}
              value={selectedBlock}
              size="medium"
              className={styles.topSelect}
              sx={{ '.MuiOutlinedInput-notchedOutline': { border: 0 } }}
            >
              {blocks.map((item) => (
                <MenuItem value={item.id}>
                  <span className={styles.topMenuItem}>{item.name}</span>
                </MenuItem>
              ))}
              <Box>
                <Button
                  type="link"
                  className={styles.addBlockBtnSelect}
                  onClick={() => {
                    setOpenAddBlock(true);
                  }}
                >
                  <div className={styles.buttonWithImage}>
                    <img src="/images/admin/plusBlue.svg" alt="" /> <span>Add Block</span>
                  </div>
                </Button>
              </Box>
            </Select>
          </div>
        ) : null}

        {isAddQuestion ? (
          <FormProvider {...methods}>
            <form>
              <AddQuestion
                languages={languages}
                idQuestion={idEditQuestion}
                methods={methods}
                allBlocks={blocks}
                sections={sections}
                selectedSectionId={selectedSection}
                setOpenAddCloseConfirm={setOpenAddCloseConfirm}
                onFinish={onFinishAddBlock}
              />
            </form>
          </FormProvider>
        ) : (
          <Table
            tableData={tableData}
            languages={languages}
            loading={loading}
            queryFilter={queryFilter}
            setQueryFilter={setQueryFilter}
            setOpenEditBlock={setOpenEditBlock}
            setNameEditBlock={setNameEditBlock}
            setIdEditBlock={setIdEditBlock}
            setIdDeleteBlock={setIdDeleteBlock}
            filter={filter}
            setFilter={setFilter}
            selectedBlock={selectedBlock}
            setIsAddBlock={setIsAddQuestion}
            allBlocks={blocks}
            setIsEditQuestion={setIsEditQuestion}
            setIdEditQuestion={setIdEditQuestion}
            reloadTableMethod={() => {
              setIsFetchTable(true);
            }}
            onDragEnd={onDragEnd}
          />
        )}
      </div>
      <ConfirmModal
        open={!!idDeleteBlock}
        title="Delete category"
        subTitle="Do you really want to delete this category?"
        onConfirm={() => {
          deleteCurrentBlock();
        }}
        onClose={() => {
          setOpenEditConfirmBlock(false);
          setIdDeleteBlock('');
        }}
      />

      <ConfirmModal
        open={openEditConfirmBlock}
        title="Change of section name"
        subTitle="Do you really want to change the name of the section?"
        onConfirm={() => {
          updateCurrentBlock();
        }}
        onClose={() => {
          setOpenEditConfirmBlock(false);
        }}
      />

      <ConfirmModal
        open={openAddCloseConfirm}
        title="Do you really want to go out?"
        subTitle="If you exit question creation mode now, all data will be lost."
        onConfirm={() => {
          confirmExitFromAdd();
          setIsAddQuestion(false);
          setIsEditQuestion(false);
          setOpenAddCloseConfirm(false);
        }}
        onClose={() => {
          setOpenAddCloseConfirm(false);
        }}
      />

      <ConfirmModal
        open={openAddBlock}
        title="Creating a section name"
        loading={loadingSections}
        subTitle={
          <Box className={styles.modalEditInputWrapper}>
            <TextField
              label="Title of section"
              value={nameAddBlock}
              onChange={(e) => setNameAddBlock(e.target.value)}
            />
          </Box>
        }
        onConfirm={() => {
          createNewBlock();
        }}
        onClose={() => {
          setNameAddBlock('');
          setOpenAddBlock(false);
        }}
        okText="Create"
      />

      <ConfirmModal
        open={openEditBlock}
        title="Editing a section name"
        loading={loadingSections}
        subTitle={
          <Box className={styles.modalEditInputWrapper}>
            <TextField
              label="Title of section"
              value={nameEditBlock}
              onChange={(e) => setNameEditBlock(e.target.value)}
            />
          </Box>
        }
        onConfirm={() => {
          setOpenEditConfirmBlock(true);
        }}
        onClose={() => {
          setOpenEditBlock(false);
          setIdEditBlock('');
          setNameEditBlock('');
        }}
        okText="Save"
      />
    </>
  );
};

export default ChatBotPage;
